import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';

/**
 * Temps passé dans un dossier (RG-24, #44, #47), ajout au contrat 1.0.0 (RFC-RG24-001). Le chronomètre de D03 enregistre chaque
 * période passée dans le dossier (entrée, inactivité, sortie) ; une correction fixe un nouveau total avec un motif obligatoire et
 * garde l'écart. Tout est comptabilisé, même ce qui n'est pas facturé. Équipe MAAT seulement.
 */
export const DUREE_MAX_PERIODE_MS = 12 * 3_600_000;

export const TempsPeriodeCreateSchema = z
  .object({ startedAt: InstantSchema, endedAt: InstantSchema })
  .refine((p) => Date.parse(p.endedAt) > Date.parse(p.startedAt), { message: 'La fin de la période doit suivre son début.' })
  .refine((p) => Date.parse(p.endedAt) - Date.parse(p.startedAt) <= DUREE_MAX_PERIODE_MS, {
    message: 'Une période dure 12 heures au plus.',
  })
  .meta({ id: 'TempsPeriodeCreate', description: 'Une période passée dans le dossier, mesurée par le chronomètre.' });

export const TempsCorrectionCreateSchema = z
  .object({ totalMs: z.number().int().min(0), reason: z.string().trim().min(1) })
  .meta({ id: 'TempsCorrectionCreate', description: 'Nouveau total du temps passé, avec le motif de la correction.' });

export const TempsEntreeSchema = z
  .object({
    id: IdSchema,
    kind: z.enum(['chrono', 'correction']),
    userId: IdSchema,
    durationMs: z.number().int(),
    startedAt: InstantSchema.nullable(),
    endedAt: InstantSchema.nullable(),
    reason: z.string().nullable(),
    createdAt: InstantSchema,
  })
  .refine((e) => (e.kind === 'correction' ? (e.reason ?? '').trim() !== '' : e.startedAt !== null && e.endedAt !== null), {
    message: 'Une correction porte un motif ; une période porte son début et sa fin.',
  })
  .meta({ id: 'TempsEntree' });

export const TempsDossierSchema = z
  .object({ dossierId: IdSchema, totalMs: z.number().int().min(0), entries: z.array(TempsEntreeSchema) })
  .meta({ id: 'TempsDossier', description: 'Temps passé dans un dossier : total, périodes et corrections (équipe MAAT).' });

export type TempsDossier = z.infer<typeof TempsDossierSchema>;
export type TempsEntree = z.infer<typeof TempsEntreeSchema>;
